import { fork, put, select, take } from 'redux-saga/effects';
import { difference } from 'lodash-es';
import { getItemsOnScreen } from './selectors';
import { waitForChangeWithDiff } from '../utils';
import {
  ITimelineGrid,
  ITimelineItem,
  ITimelineSurvey,
  ITimelineItemDismissDescriptor,
  ITimelineItemEnterDescriptor,
  ITimelineItemPreExitDescriptor,
} from '../../types/types';
import { requestPause } from '../videoState/actions';
import { hasActionOnEnter, hasActionOnPreExit } from '../../types/guards';
import { getCurrentTime } from '../videoState/selectors';
import { getPlayerMode } from '../sourceConfiguration/selectors';
import { toggleAlternativeDurationLine } from '../miscScreenObjects/actions';
import { PlayerMode } from '../../components/types/defaultPropTypes';
import { getPlayerExternalAPI } from '../selectors';
import { PlayerExternalAPI } from '../../api/external/PlayerExternalAPI';

const itemEnterEmitters = {
  button: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineButtonEnter,
  image: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineImageEnter,
  grid: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineGridEnter,
  survey: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineSurveyEnter,
};

const itemPreExitEmitters = {
  button: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineButtonPreExit,
  image: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineImagePreExit,
  grid: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineGridPreExit,
  survey: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineSurveyPreExit,
};

const itemExitEmitters = {
  button: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineButtonExit,
  image: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineImageExit,
  grid: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineGridExit,
  survey: (playerExternalApi: PlayerExternalAPI) =>
    playerExternalApi?.fromEmitter.timelineSurveyExit,
};

const isTimelineGrid = (item: ITimelineItem): item is ITimelineGrid => {
  return item.type === 'grid';
};

const isTimelineSurvey = (item: ITimelineItem): item is ITimelineSurvey => {
  return item.type === 'survey';
};

const toPlayerExternalAPITimelineItemForEnter = (
  item: ITimelineItem
): ITimelineItemEnterDescriptor | undefined => {
  if (!hasActionOnEnter(item)) {
    return;
  }

  return {
    id: item.id,
    type: item.type,
    actionsOnEnter: item.actionsOnEnter,
  };
};

const toPlayerExternalAPITimelineItemForPreExit = (
  item: ITimelineItem
): ITimelineItemPreExitDescriptor | undefined => {
  if (!hasActionOnPreExit(item)) {
    return;
  }

  return {
    id: item.id,
    type: item.type,
    actionsOnPreExit: item.actionsOnPreExit,
  };
};

const toPlayerExternalAPITimelineItemForExit = (
  item: ITimelineItem
): ITimelineItemDismissDescriptor | undefined => {
  return {
    id: item.id,
    type: item.type,
  };
};

const getGridWithDurationLine = (items: ITimelineItem[]) => {
  const grid = items.find((x) => isTimelineGrid(x) || isTimelineSurvey(x)) as
    | ITimelineGrid
    | ITimelineSurvey
    | undefined;

  if (
    grid &&
    grid.actionsOnEnter.find((x) => x.type === 'pause') &&
    grid.pauseAutoResumeIn > 0 &&
    !grid.isRequired
  ) {
    return grid;
  }

  return undefined;
};

function* handleEnter(items: ITimelineItem[]) {
  const playerExternalApi = getPlayerExternalAPI(yield select());

  const newItemRequirePause = items.some((x) => {
    if (x.type === 'shareGate' || x.type === 'turnstile') return true;

    if (hasActionOnEnter(x)) {
      return x.actionsOnEnter.some((action) => action.type === 'pause');
    }

    return false;
  });

  if (newItemRequirePause) {
    // TODO: don't trigger overlay
    yield put(requestPause({}));
  }

  const grid = getGridWithDurationLine(items);
  if (getPlayerMode(yield select()) === PlayerMode.NORMAL && grid) {
    yield put(
      toggleAlternativeDurationLine({
        color: 0xf334c2,
        percents: 0,
        duration: grid.pauseAutoResumeIn,
        triggeredByItemId: grid.id,
        startAtTime: grid.startSecond,
      })
    );
  }

  items.forEach((item) => {
    const itemToPass = toPlayerExternalAPITimelineItemForEnter(item);
    if (!itemToPass) {
      return;
    }

    const emitter = itemEnterEmitters[item.type]?.(playerExternalApi);
    emitter?.emit(itemToPass);
  });
}

function* handleExit(items: ITimelineItem[]) {
  const playerExternalApi = getPlayerExternalAPI(yield select());

  if (
    getPlayerMode(yield select()) === PlayerMode.NORMAL &&
    getGridWithDurationLine(items)
  ) {
    yield put(toggleAlternativeDurationLine(undefined));
  }

  items.forEach((item) => {
    const itemToPass = toPlayerExternalAPITimelineItemForExit(item);
    if (!itemToPass) {
      return;
    }

    const emitter = itemExitEmitters[item.type]?.(playerExternalApi);
    emitter?.emit(itemToPass);
  });
}

function* handlePreExit(items: ITimelineItem[]) {
  const playerExternalApi = getPlayerExternalAPI(yield select());
  const requirePause = items.some((x) => {
    if (hasActionOnPreExit(x)) {
      return x.actionsOnPreExit.some((action) => action.type === 'pause');
    }

    return false;
  });

  if (requirePause) {
    // TODO: don't trigger overlay
    yield put(requestPause({}));
  }

  items.forEach((item) => {
    const itemToPass = toPlayerExternalAPITimelineItemForPreExit(item);
    if (!itemToPass) {
      return;
    }

    const emitter = itemPreExitEmitters[item.type]?.(playerExternalApi);
    emitter?.emit(itemToPass);
  });
}

function* exitAndEnterWatcher() {
  while (true) {
    yield take();

    const [prevItems, currentItems]: [
      ReturnType<typeof getItemsOnScreen>,
      ReturnType<typeof getItemsOnScreen>
    ] = yield waitForChangeWithDiff(getItemsOnScreen);

    const newItems = difference(currentItems, prevItems);
    const removedItems = difference(prevItems, currentItems);

    yield handleEnter(newItems);
    yield handleExit(removedItems);
  }
}

// Pre exit should happen before 1s of the item duration end
function* preExitWatcher() {
  let alreadyPreExitedItemIds: string[] = [];

  while (true) {
    yield take();
    const currentItems = getItemsOnScreen(yield select());
    const currentItemIds = currentItems.map(({ id }) => id);

    const currentTime = getCurrentTime(yield select());
    const itemsToPreExit = currentItems.filter(
      // eslint-disable-next-line no-loop-func
      (item) =>
        item.endSecond - 1 <= currentTime &&
        currentTime <= item.endSecond &&
        !alreadyPreExitedItemIds.includes(item.id)
    );

    yield handlePreExit(itemsToPreExit);
    alreadyPreExitedItemIds.push(...itemsToPreExit.map(({ id }) => id));

    // Remove those items that are not on screen already
    alreadyPreExitedItemIds = alreadyPreExitedItemIds.filter((id) =>
      currentItemIds.includes(id)
    );
  }
}

export function* itemLifecycleSaga() {
  yield fork(exitAndEnterWatcher);
  yield fork(preExitWatcher);
}
